import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { formatApiErrorDetail } from "@/AuthContext";

import { API } from "./api";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await fetch(`${API}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, new_password: password }),
      }).then(async (r) => {
        if (!r.ok) throw Object.assign(new Error(), { response: { data: await r.json() } });
        return r.json();
      });
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-sm bg-[#0a1c46] border border-white/10 rounded-2xl p-8">
        {done ? (
          <div data-testid="reset-success">
            <h1 className="text-xl font-bold text-[#ffcd3c]">Password updated</h1>
            <p className="mt-3 text-sm text-blue-100/90">You can sign in with your new password now.</p>
            <Link
              to="/login"
              data-testid="reset-success-login-link"
              className="mt-6 block text-center rounded-full bg-[#ffcd3c] text-[#0a1a3f] font-bold py-3 hover:brightness-110 transition"
            >
              Go to Sign In
            </Link>
          </div>
        ) : (
          <>
            <h1 className="text-xl font-bold text-white">Choose a new password</h1>
            {!token && (
              <p data-testid="reset-missing-token" className="mt-3 text-sm text-red-300">
                This reset link is missing its token. Request a new one from the sign-in page.
              </p>
            )}
            <form onSubmit={submit} className="mt-6 space-y-4">
              <input
                data-testid="reset-password-input"
                type="password"
                required
                minLength={8}
                placeholder="New password (8+ characters)"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-lg bg-[#04102e] border border-white/15 px-4 py-3 text-white placeholder-blue-200/40 focus:outline-none focus:border-[#ffcd3c]"
              />
              <input
                data-testid="reset-confirm-input"
                type="password"
                required
                placeholder="Confirm new password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full rounded-lg bg-[#04102e] border border-white/15 px-4 py-3 text-white placeholder-blue-200/40 focus:outline-none focus:border-[#ffcd3c]"
              />
              {error && (
                <div data-testid="reset-error" className="rounded-lg bg-red-500/15 border border-red-400/40 text-red-200 text-sm px-4 py-3">
                  {error}
                </div>
              )}
              <button
                data-testid="reset-submit-btn"
                type="submit"
                disabled={busy || !token}
                className="w-full rounded-full bg-[#ffcd3c] text-[#0a1a3f] font-bold py-3 hover:brightness-110 transition disabled:opacity-50"
              >
                {busy ? "Saving…" : "Update Password"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
